import React from 'react'
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import TaskIcon from "@mui/icons-material/Task";
import {
    useBookingConfirmationDocumentCreate,
    useBookingConfirmationDocumentPolling,
    useSendBookingConfirmationDocumentEmail
} from "../../query";
import LoadingButton from "@mui/lab/LoadingButton";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {dateAndTime} from "../../../../services/date/formatters";
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CircularProgress from "@mui/material/CircularProgress";
import SendIcon from "@mui/icons-material/Send";

const BookingConfirmationDocument = ({booking}) => {
    const bookingConfirmationDocument = booking.bookingConfirmationDocument;
    const createdAt = bookingConfirmationDocument?.createdAt;
    const emailSentAt = bookingConfirmationDocument?.emailSentAt;
    const fileUrl = bookingConfirmationDocument?.fileUrl;
    const downloadFileUrl = bookingConfirmationDocument?.downloadFileUrl;
    const pdfStatus = bookingConfirmationDocument?.pdfStatus;
    const emailStatus = bookingConfirmationDocument?.emailStatus;

    const bookingConfirmationDocumentCreate = useBookingConfirmationDocumentCreate({bookingId: booking.id});
    const sendBookingConfirmationDocumentEmail = useSendBookingConfirmationDocumentEmail({bookingId: booking.id});

    const editDisabled = !booking.isEditable();

    const handleCreate = () => {
        bookingConfirmationDocumentCreate.mutate({bookingId: booking.id});
    }

    const handleSendEmail = () => {
        sendBookingConfirmationDocumentEmail.mutate({bookingId: booking.id});
    }

    const pdfCompleted = pdfStatus === 'completed';
    const pdfPending = pdfStatus === 'pending';
    const pdfFailed = pdfStatus === 'failed';

    const emailCompleted = emailStatus === 'completed';
    const emailPending = emailStatus === 'pending';
    const emailFailed = emailStatus === 'failed';

    // Polling to update the status of the document as long as it's pending
    useBookingConfirmationDocumentPolling(booking.id, pdfPending || emailPending);

    if (!bookingConfirmationDocument) {
        return <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <Avatar sx={{bgcolor: 'warning.main', width: 48, height: 48, fontSize: 14}}>
                <TaskIcon/>
            </Avatar>
            <Stack direction="row" spacing={2} rowGap={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" flexGrow={1}>
                <Stack>
                    <Typography variant="body1" fontWeight={600}>Documento di conferma prenotazione</Typography>
                    <Typography variant="body2">Documento di conferma prenotazione non presente</Typography>
                    <Typography variant="body2">Puoi creare e successivamente inviare il documento di conferma cliccando
                        su "Crea Documento di Conferma"</Typography>
                </Stack>
                <LoadingButton variant="outlined"
                               color="primary"
                               size="small"
                               onClick={handleCreate}
                               loading={bookingConfirmationDocumentCreate.isLoading}
                               disabled={editDisabled}
                               sx={{alignSelf: "flex-end"}}>
                    Crea Documento di Conferma
                </LoadingButton>
            </Stack>
        </Stack>
    } else {
        return <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{bgcolor: 'warning.main', width: 48, height: 48, fontSize: 14}}>
                <TaskIcon/>
            </Avatar>
            <Stack direction="row" rowGap={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" flexGrow={1}>
                <Stack>
                    <Typography variant="body1" fontWeight={600}>Documento di conferma prenotazione</Typography>
                    {
                        <Typography variant="body2">
                            Creato in data: {dateAndTime(createdAt)}
                        </Typography>
                    }
                    {
                        pdfCompleted && <Typography variant="body2" color="success.main">
                            Documento PDF creato con successo.
                        </Typography>
                    }
                    {
                        pdfPending && <Stack direction={"row"} spacing={1} alignItems="center">
                            <Typography variant="body2">
                                Creazione documento PDF in corso...
                            </Typography>
                            <CircularProgress size={16} color="primary"/>
                        </Stack>
                    }
                    {
                        pdfFailed && <Typography variant="body2" color="error">
                            Generazione documento non riuscita. Riprova a creare il documento.
                        </Typography>
                    }
                    {
                        emailCompleted && <Typography variant="body2" color="success.main">
                            Inviato all'ospite tramite email in data: {dateAndTime(emailSentAt)}
                        </Typography>
                    }
                    {
                        emailPending && <Stack direction={"row"} spacing={1} alignItems="center">
                            <Typography variant="body2">
                                Invio email in corso...
                            </Typography>
                            <CircularProgress size={16} color="primary"/>
                        </Stack>
                    }
                    {
                        emailFailed && <Typography variant="body2" color="error">
                            Invio email non riuscito, ricontrollare il campo email dell'ospite e riprovare.
                        </Typography>
                    }
                    {
                        <LoadingButton color="primary"
                                       size="small"
                                       onClick={handleCreate}
                                       loading={bookingConfirmationDocumentCreate.isLoading}
                                       disabled={editDisabled || pdfPending || emailPending}
                                       startIcon={<RefreshIcon/>}
                                       sx={{alignSelf: "flex-start"}}
                        >
                            Aggiorna Documento
                        </LoadingButton>
                    }
                </Stack>
                <Stack direction="row" alignItems="center" sx={{alignSelf: "flex-end", rowGap: 2, columnGap: 1}} flexWrap={"wrap"}>
                    <LoadingButton variant="outlined"
                                   startIcon={<SendIcon/>}
                                   color="primary"
                                   size="small"
                                   loading={emailPending || sendBookingConfirmationDocumentEmail.isLoading}
                                   disabled={editDisabled || !pdfCompleted || emailCompleted || bookingConfirmationDocumentCreate.isLoading}
                                   onClick={handleSendEmail}>
                        Invia
                    </LoadingButton>

                    <Button variant="outlined"
                            startIcon={<FileDownloadIcon/>}
                            color="primary"
                            size="small"
                            href={downloadFileUrl}
                            rel="noopener"
                            disabled={!pdfCompleted || pdfPending || bookingConfirmationDocumentCreate.isLoading}
                            target="_self">
                        Scarica
                    </Button>

                    <Button variant="outlined"
                            startIcon={<VisibilityIcon/>}
                            color="primary"
                            size="small"
                            href={fileUrl}
                            disabled={!pdfCompleted || pdfPending || bookingConfirmationDocumentCreate.isLoading}
                            target="_blank">
                        Apri
                    </Button>
                </Stack>
            </Stack>
        </Stack>
    }
}


export default BookingConfirmationDocument;